import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "@/api/client";
import { parseApiResponse } from "@/api/responseSchemas";

const authorizationRequestSchema = z.object({
  client_id: z.string(),
  client_name: z.string(),
  redirect_uri: z.string(),
  resource: z.string(),
  scopes: z.array(z.string()),
  created_at: z.string(),
});

const grantSchema = z.object({
  id: z.number(),
  client_id: z.string(),
  client_name: z.string(),
  scopes: z.array(z.string()),
  created_at: z.string(),
  last_used_at: z.string().nullable(),
  expires_at: z.string(),
  revoked_at: z.string().nullable(),
});

const decisionResponseSchema = z.object({ redirect_url: z.string().min(1) });
const packageSchema = z.object({
  version: z.string(),
  download_url: z.string().url(),
  mcp_url: z.string().url(),
  clients: z.array(z.string()),
});

export type McpAuthorizationRequest = z.infer<typeof authorizationRequestSchema>;
export type McpGrant = z.infer<typeof grantSchema>;

export const mcpGrantsQueryKey = (userId: string | undefined) => ["mcpGrants", userId] as const;
const MCP_PACKAGE_QUERY_KEY = ["mcpPackage"] as const;

export const useMcpPackage = () =>
  useQuery({
    queryKey: MCP_PACKAGE_QUERY_KEY,
    queryFn: getMcpPackage,
  });

export const getMcpPackage = async () => {
  const response = await apiClient.get("/mcp/package");
  return parseApiResponse(packageSchema, response.data, "GET /mcp/package");
};

export const useMcpGrants = (userId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: mcpGrantsQueryKey(userId),
    queryFn: getMcpGrants,
    enabled: enabled && userId !== undefined,
  });

export const getMcpGrants = async () => {
  const response = await apiClient.get("/mcp-grants");
  return parseApiResponse(z.array(grantSchema), response.data, "GET /mcp-grants");
};

export const getMcpAuthorizationRequest = async (requestId: string) => {
  const response = await apiClient.get(`/oauth/authorize-requests/${encodeURIComponent(requestId)}`);
  return parseApiResponse(
    authorizationRequestSchema,
    response.data,
    "GET /oauth/authorize-requests/{request_id}",
  );
};

export const submitMcpAuthorizationDecision = async (
  requestId: string,
  decision: "approve" | "deny",
) => {
  const response = await apiClient.post(
    `/oauth/authorize-requests/${encodeURIComponent(requestId)}/decision`,
    { decision },
  );
  return parseApiResponse(
    decisionResponseSchema,
    response.data,
    "POST /oauth/authorize-requests/{request_id}/decision",
  );
};

export const revokeMcpGrant = (grantId: number) =>
  apiClient.delete(`/mcp-grants/${grantId}`);
